import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { analyzeSchema, createFileRecord, fetchClientTableNames, fetchUploadHistory } from '../api/fileUpload'
import { queryKeys } from '../api/queryKeys'
import type { UploadHistoryPage, UploadMode } from '../types/fileUpload'

// Poll cadence while an upload is still being processed.
const HISTORY_POLL_INTERVAL_MS = 5000
const STALE_AFTER_MS = 15 * 60 * 1000

function isTerminalStatus(status: string) {
  const normalized = (status || '').toLowerCase()
  return (
    normalized.includes('completed') ||
    normalized.includes('success') ||
    normalized.includes('uploaded') ||
    normalized.includes('failed') ||
    normalized.includes('error')
  )
}

// A row worth polling for: not yet terminal AND recently created/updated.
function isActivelyProcessing(row: UploadHistoryPage['items'][number]) {
  if (isTerminalStatus(row.status)) return false
  const timestamp = Date.parse(row.uploadedAt || '')
  if (Number.isNaN(timestamp)) return false
  return Date.now() - timestamp < STALE_AFTER_MS
}

export function useUploadHistory(params: { clientId?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: queryKeys.fileUpload.history({
      clientId: params.clientId || '',
      page: params.page,
      pageSize: params.pageSize,
    }),
    queryFn: () => fetchUploadHistory(params),
    // Lightweight polling: keep refetching only while a recent upload is still
    // in progress; stop once every row is terminal or stale.
    refetchInterval: (query) => {
      const items = (query.state.data as UploadHistoryPage | undefined)?.items ?? []
      const hasActive = items.some(isActivelyProcessing)
      return hasActive ? HISTORY_POLL_INTERVAL_MS : false
    },
  })
}

export function useClientTableNames(clientId?: string) {
  return useQuery({
    queryKey: queryKeys.fileUpload.tables(clientId),
    queryFn: () => fetchClientTableNames(clientId),
  })
}

export function useAnalyzeSchemaMutation() {
  return useMutation({
    mutationFn: (input: { file: File; tableName: string; uploadMode: UploadMode; clientId?: string }) => analyzeSchema(input),
  })
}

export function useCreateFileRecordMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { file: File; tableName: string; uploadMode: UploadMode; clientId?: string }) =>
      createFileRecord(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fileUpload.all })
    },
  })
}
